export const ZERO_PENCE = 0;
/**
 * The largest magnitude a `Pence` value may hold and still be an EXACT integer:
 * `Number.MAX_SAFE_INTEGER` (≈ £90.07 trillion). Beyond ±this, JS number
 * arithmetic silently loses integer precision, which would emit a wrong penny.
 * The arithmetic helpers below fail loud (throw) rather than return a lossy sum.
 */
export const MAX_SAFE_PENCE = Number.MAX_SAFE_INTEGER;
/** Guard that an arithmetic result is still an exact integer number of pence. */
function assertSafePence(value, op) {
    if (!Number.isSafeInteger(value)) {
        throw new RangeError(`Pence ${op} left the exact-integer range (|result| > ${Number.MAX_SAFE_INTEGER}); ` +
            'the value would no longer be penny-exact.');
    }
    return value;
}
/** Construct a `Pence` value, rejecting non-integers. */
export function pence(value) {
    if (!Number.isInteger(value)) {
        throw new RangeError(`Pence must be a whole number of pence, received: ${value}`);
    }
    return value;
}
export function isPence(value) {
    return Number.isInteger(value);
}
/** Convert whole pounds to pence. */
export function poundsToPence(pounds) {
    return pence(Math.round(pounds * 100));
}
export function addPence(a, b) {
    return assertSafePence(a + b, 'addition');
}
export function subPence(a, b) {
    return assertSafePence(a - b, 'subtraction');
}
/**
 * Multiply a pence amount by a small non-negative integer count (e.g. 12 months
 * or 4 quarters). Kept as a dedicated helper so the product is overflow-checked
 * like every other pence operation rather than being a bare `*` in a hot loop.
 */
export function mulPence(amount, count) {
    if (!Number.isInteger(count) || count < 0) {
        throw new RangeError(`Pence multiplier must be a non-negative integer, received: ${count}`);
    }
    return assertSafePence(amount * count, 'multiplication');
}
/**
 * Scale of the engine's cumulative price deflator: parts-per-million, so
 * `deflatorPpm === 1_000_000` in the projection's start year and grows by the
 * CPI assumption each year (integer trunc arithmetic — byte-deterministic).
 */
export const DEFLATOR_PPM_START = 1_000_000;
/**
 * Convert a nominal pence figure into today's-value terms using the per-year
 * cumulative `deflatorPpm` the engine emits. This is the ONE deflation helper —
 * the web chart toggle, reports and tests all deflate through it so a
 * today's-value figure can never drift between surfaces.
 */
export function toTodaysPence(nominal, deflatorPpm) {
    if (!Number.isInteger(deflatorPpm) || deflatorPpm <= 0) {
        throw new RangeError(`deflatorPpm must be a positive integer, received: ${deflatorPpm}`);
    }
    if (!Number.isSafeInteger(nominal)) {
        throw new RangeError(`Pence deflation needs an exact-integer input, received: ${nominal}. ` +
            'A figure this large is a data error upstream, not a deflation result.');
    }
    // Scaled through BigInt, NOT double arithmetic: `nominal * 1_000_000` exceeds
    // the 53-bit exact range once the figure passes ~£90m, and the rounding of
    // that intermediate can shift the truncated quotient by a penny (verified:
    // ~3 in 1e6 large-figure cases). BigInt division truncates toward zero, which
    // is the same direction the previous `Math.trunc` took, so every figure that
    // was already exact is unchanged — this only removes the penny drift.
    const scaled = (BigInt(nominal) * BigInt(DEFLATOR_PPM_START)) / BigInt(deflatorPpm);
    return assertSafePence(Number(scaled), 'deflation');
}
/** Format pence as a GBP string, e.g. 123456 -> "£1,234.56". */
export function formatGbp(value) {
    const negative = value < 0;
    const abs = Math.abs(value);
    const pounds = Math.floor(abs / 100);
    const remainder = abs % 100;
    return `${negative ? '-' : ''}£${pounds.toLocaleString('en-GB')}.${remainder
        .toString()
        .padStart(2, '0')}`;
}
/**
 * Format pence as WHOLE pounds, e.g. 844720341 -> "£8,447,203". Display-only
 * rounding for headline/net-worth figures where the pence tail is just noise;
 * the stored value stays exact integer pence. Rounds to the nearest pound.
 */
export function formatGbpWhole(value) {
    const negative = value < 0;
    const pounds = Math.round(Math.abs(value) / 100);
    return `${negative ? '-' : ''}£${pounds.toLocaleString('en-GB')}`;
}
//# sourceMappingURL=money.js.map